import { z } from 'zod';
import { UuidSchema } from './primitives/uuid.js';
import { ProfileSummarySchema } from './profiles.js';
import { PromptOverridesInputSchema } from './shared/analysis.js';
import { ExternalLinkContentMetadataSchema } from './shared/link-content.js';
import { type SupportedTimeRange, TimeRangeSchema } from './time-range.js';
import { UITimelineAnalysisSchema } from './timeline-analysis-ui.js';

export {
  PromptOverrideSectionInputSchema as TimelineAnalysisPromptOverrideSectionSchema,
  PromptOverridesInputSchema as TimelineAnalysisPromptOverridesSchema,
} from './shared/analysis.js';
export {
  TIME_RANGE_VALUES as TIMELINE_ANALYSIS_TIME_RANGES,
  TimeRangeSchema as TimelineAnalysisTimeRangeSchema,
} from './time-range.js';

// Downstream consumers: timeline API persistence (src/app/api/timeline/lib/persistence.ts),
// home timeline sync (src/app/api/timeline/lib/home/sync.ts), and the CLI/email exporters.
export const TIMELINE_ANALYSIS_REPORT_LENGTHS = ['compact', 'normal', 'extended'] as const;

export type TimelineAnalysisTimeRange = SupportedTimeRange;

export const TimelineAnalysisReportLengthSchema = z.enum(TIMELINE_ANALYSIS_REPORT_LENGTHS);
export type TimelineAnalysisReportLength = (typeof TIMELINE_ANALYSIS_REPORT_LENGTHS)[number];

const booleanQueryParam = z.union([z.string(), z.number(), z.boolean()]).transform((value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
    return true;
  }
  if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
    return false;
  }
  return normalized.length === 0 ? true : Boolean(normalized);
});

export const TimelineAnalysisPreferenceSchema = z.boolean().optional();

export const TimelineAnalysisQuerySchema = z
  .object({
    timeRange: TimeRangeSchema.optional().default('6h'),
    includeRetweets: booleanQueryParam.optional().default(true),
    includeReplies: booleanQueryParam.optional().default(true),
    minPosts: z.coerce.number().int().min(1).optional().default(1),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(0).max(200).optional().default(25),
    sortField: z
      .enum(['user', 'posts', 'percentage', 'originalPosts', 'links', 'replies', 'quotes', 'interactions'])
      .optional()
      .default('posts'),
    sortDirection: z.enum(['asc', 'desc']).optional().default('desc'),
    timezone: z.string().optional(),
    format: z.enum(['json', 'cli']).optional().default('json'),
  })
  .transform((value) => ({
    ...value,
    includeRetweets: Boolean(value.includeRetweets),
    includeReplies: Boolean(value.includeReplies),
  }));

export type TimelineAnalysisQuery = z.infer<typeof TimelineAnalysisQuerySchema>;

export const TimelineAnalysisRequestSchema = z.object({
  timeRange: TimeRangeSchema.default('6h'),
  reportLength: TimelineAnalysisReportLengthSchema.optional(),
  immediate: z.boolean().optional(),
  model: z.string().optional(),
  fromTimestamp: z.date().optional(),
  toTimestamp: z.date().optional(),
  maxTweets: z.number().int().positive().max(5000).optional(),
  forceNew: z.boolean().optional(),
  preferenceSignals: TimelineAnalysisPreferenceSchema,
  promptOverrides: PromptOverridesInputSchema.optional(),
  format: z.enum(['json', 'cli']).optional().default('json'),
  fetchExternalUrls: z.boolean().optional(),
  maxLinkSummaries: z.number().int().min(0).max(50).optional(),
});

export type TimelineAnalysisRequest = z.infer<typeof TimelineAnalysisRequestSchema>;

export const TimelineAnalysisRealtimeTokenSchema = z
  .object({
    channel: z.string(),
    topics: z.array(z.string()),
    key: z.string().nullable().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

export const TimelineAnalysisCachedEntrySchema = z.object({}).passthrough().nullable();

export const TimelineAnalysisMetadataPayloadSchema = z
  .object({
    model: z.string().optional().nullable(),
    requestedModel: z.string().optional().nullable(),
    modelSelectionReason: z.string().optional().nullable(),
    reportLength: TimelineAnalysisReportLengthSchema.optional().nullable(),
    generationTimeMs: z.number().optional().nullable(),
    tweetCount: z.number().optional().nullable(),
    mentionCount: z.number().optional().nullable(),
    timelineTweetCount: z.number().optional().nullable(),
    timeWindowStart: z.coerce.date().optional().nullable(),
    timeWindowEnd: z.coerce.date().optional().nullable(),
    latestTweetAt: z.coerce.date().optional().nullable(),
    createdAt: z.coerce.date().optional().nullable(),
    immediateRequest: z.boolean().optional().nullable(),
    reasoningEnabled: z.boolean().optional().nullable(),
    tweetLimit: z.number().optional().nullable(),
    timelineWindowStart: z.coerce.date().optional().nullable(),
    timelineWindowEnd: z.coerce.date().optional().nullable(),
    fetchExternalUrls: z.boolean().optional().nullable(),
    maxLinkSummaries: z.number().int().min(0).max(50).optional().nullable(),
    linkContent: ExternalLinkContentMetadataSchema,
  })
  .passthrough()
  .nullable();

export const TimelineAnalysisResultSchema = UITimelineAnalysisSchema.nullable();
// Referenced by server persistence, inngest exporters, and UI loaders to ensure a single analysis payload shape.

export const TimelineAnalysisStartDataSchema = z.object({
  analysisId: UuidSchema,
  cached: z.boolean(),
  metadata: TimelineAnalysisMetadataPayloadSchema.optional(),
  analysis: TimelineAnalysisResultSchema.optional(),
  token: TimelineAnalysisRealtimeTokenSchema,
});

const TimelineAnalysisStartWrappedSchema = z
  .object({
    success: z.literal(true),
    data: TimelineAnalysisStartDataSchema,
    message: z.string().nullable().optional(),
  })
  .passthrough();

const TimelineAnalysisStartCliSchema = z
  .object({
    analysisId: UuidSchema,
    cached: z.boolean(),
    metadata: TimelineAnalysisMetadataPayloadSchema.optional(),
    analysis: TimelineAnalysisResultSchema.optional(),
    token: TimelineAnalysisRealtimeTokenSchema,
    message: z.string().optional(),
  })
  .passthrough();

export const TimelineAnalysisStartResponseSchema = z.union([
  TimelineAnalysisStartWrappedSchema,
  TimelineAnalysisStartCliSchema,
]);

export type TimelineAnalysisStartResponse = z.infer<typeof TimelineAnalysisStartResponseSchema>;

export const TimelineAnalysisCacheResponseSchema = z.object({
  format: z.enum(['json', 'cli']).default('json'),
  status: z.enum(['ready', 'missing']).default('ready'),
  analysisId: z.string().min(1).nullable(),
  analysis: TimelineAnalysisResultSchema.nullable(),
  metadata: TimelineAnalysisMetadataPayloadSchema.nullable(),
  isStale: z.boolean(),
  newTweetCount: z.number().int().nonnegative(),
  message: z.string().nullable().optional(),
});

export type TimelineAnalysisCacheResponse = z.infer<typeof TimelineAnalysisCacheResponseSchema>;

export const TimelineAnalysisHistoryProfileSchema = ProfileSummarySchema.extend({
  verified: z.boolean(),
});

export const TimelineAnalysisHistoryReportSchema = z.object({
  id: z.string(),
  userId: z.string(),
  timeRange: TimeRangeSchema,
  analysis: TimelineAnalysisResultSchema,
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.date(),
  expiresAt: z.date().nullable(),
  lastAccessedAt: z.date().nullable(),
});

export const TimelineAnalysisHistoryResponseSchema = z.object({
  reports: z.array(TimelineAnalysisHistoryReportSchema),
  newTweetCounts: z.record(z.string(), z.number()),
  tweets: z.record(z.string(), z.unknown()).optional(),
  profiles: z.record(z.string(), TimelineAnalysisHistoryProfileSchema).optional(),
  urlMetadata: z.record(z.string(), z.unknown()).optional(),
});

export type TimelineAnalysisHistoryResponse = z.infer<typeof TimelineAnalysisHistoryResponseSchema>;

export const TimelineAnalysisHistoryDeleteResponseSchema = z.object({
  success: z.literal(true),
  deletedCount: z.number().int().nonnegative(),
});

export const TimelineAnalysisStatusResponseSchema = z
  .object({
    status: z.string(),
    analysisId: z.string(),
    analysisReady: z.boolean().optional(),
    retryAfter: z.number().optional(),
    error: z.string().nullable().optional(),
    message: z.string().nullable().optional(),
    startedAt: z.date().nullable().optional(),
    completedAt: z.date().nullable().optional(),
    wasStale: z.boolean().optional(),
  })
  .passthrough();

export type TimelineAnalysisStatusResponse = z.infer<typeof TimelineAnalysisStatusResponseSchema>;

export const TimelineAnalysisHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  format: z.enum(['json', 'cli']).optional().default('json'),
});

export const TimelineAnalysisStatusQuerySchema = z.object({
  analysisId: z.string(),
  format: z.enum(['json', 'cli']).optional(),
});
export type TimelineAnalysisStatusQuery = z.infer<typeof TimelineAnalysisStatusQuerySchema>;

export const TimelineAnalysisStatusPathParamsSchema = z.object({
  analysisId: z.string(),
});

export type TimelineAnalysisStatusPathParams = z.infer<typeof TimelineAnalysisStatusPathParamsSchema>;

export const TimelineAnalysisCancelPathParamsSchema = z.object({ analysisId: UuidSchema });
export type TimelineAnalysisCancelPathParams = z.infer<typeof TimelineAnalysisCancelPathParamsSchema>;

export const TimelineAnalysisCancelRequestSchema = z.undefined();
export type TimelineAnalysisCancelRequest = z.infer<typeof TimelineAnalysisCancelRequestSchema>;

export const TimelineAnalysisDeletePathParamsSchema = z.object({ analysisId: z.string() });
export type TimelineAnalysisDeletePathParams = z.infer<typeof TimelineAnalysisDeletePathParamsSchema>;

export const TimelineAnalysisInfoQuerySchema = z.object({
  timeRange: TimeRangeSchema.optional().default('24h'),
});

export const TimelineAnalysisActivityBucketSchema = z.object({
  start: z.date(),
  end: z.date(),
  total: z.number(),
  original: z.number(),
  replies: z.number(),
  links: z.number(),
  quotes: z.number(),
});

export const TimelineAnalysisInfoResponseSchema = z.object({
  tweetCount: z.number(),
  timeRange: TimeRangeSchema,
  startDate: z.date(),
  endDate: z.date(),
  countSource: z.enum(['snapshots', 'database']).optional(),
  activity: z
    .object({
      bucketMinutes: z.number(),
      buckets: z.array(TimelineAnalysisActivityBucketSchema),
    })
    .optional(),
  snapshotSummary: z
    .object({
      count: z.number(),
      earliest: z.date().nullable(),
      latest: z.date().nullable(),
    })
    .nullable()
    .optional(),
  coverage: z
    .object({
      dataComplete: z.boolean().optional(),
      needsSync: z.boolean().optional(),
      coveragePercent: z.number().optional(),
      maxTweets: z.number().nullable().optional(),
      capReached: z.boolean(),
      oldestDataPoint: z.date().nullable(),
      newestDataPoint: z.date().nullable(),
      tweetCountInWindow: z.number(),
    })
    .optional(),
  activeJob: z
    .object({
      analysisId: z.string(),
      status: z.string(),
      createdAt: z.date(),
      inngestRunId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  cachedAnalysis: z
    .object({
      id: z.string(),
      completedAt: z.date().nullable(),
      tweetCount: z.number(),
      reportLength: TimelineAnalysisReportLengthSchema,
      isStale: z.boolean(),
      analysisCreatedAt: z.date(),
    })
    .nullable()
    .optional(),
  newSinceLast: z.number(),
  profileStats: z
    .object({
      followersCount: z.number(),
      followingCount: z.number(),
    })
    .nullable()
    .optional(),
});

export type TimelineAnalysisInfoResponse = z.infer<typeof TimelineAnalysisInfoResponseSchema>;

const NotificationPreferencesSchema = z.object({
  enabled: z.boolean(),
  analysis_complete: z.boolean().optional(),
  system: z.boolean().optional(),
  import_complete: z.boolean().optional(),
});

export const TimelineAnalysisSettingsSchema = z.object({
  userId: z.string(),
  reportLength: TimelineAnalysisReportLengthSchema,
  notifications: z.record(z.string(), z.unknown()),
  pushNotifications: NotificationPreferencesSchema,
  emailNotifications: NotificationPreferencesSchema,
  dailyDigest: z.record(z.string(), z.unknown()).optional(),
  retentionDays: z.number().optional(),
  maxConcurrentReports: z.number().optional(),
  promptProfile: z.unknown(),
  dailyDigestQuota: z
    .object({
      limit: z.number().nullable(),
      used: z.number(),
      remaining: z.number().nullable(),
    })
    .optional(),
});

export type TimelineAnalysisSettingsResponse = z.infer<typeof TimelineAnalysisSettingsSchema>;

const PushNotificationUpdateSchema = z
  .object({
    enabled: z.boolean().optional(),
    analysis_complete: z.boolean().optional(),
    system: z.boolean().optional(),
  })
  .partial();

const EmailNotificationUpdateSchema = z
  .object({
    enabled: z.boolean().optional(),
    import_complete: z.boolean().optional(),
    system: z.boolean().optional(),
  })
  .partial();

export const TimelineAnalysisSettingsUpdateSchema = z.object({
  reportLength: TimelineAnalysisReportLengthSchema.optional(),
  retentionDays: z.number().int().min(1).max(365).optional(),
  maxConcurrentReports: z.number().int().min(1).max(5).optional(),
  dailyDigest: z.record(z.string(), z.unknown()).optional(),
  notifications: z.record(z.string(), z.unknown()).optional(),
  pushNotifications: z.union([z.boolean(), PushNotificationUpdateSchema]).optional(),
  emailNotifications: z.union([z.boolean(), EmailNotificationUpdateSchema]).optional(),
  promptProfile: z.unknown().optional().nullable(),
});

export const TimelineAnalysisDeleteResponseSchema = z.object({
  success: z.literal(true),
});

export const TimelineAnalysisRealtimeTokenRequestSchema = z.object({
  analysisId: UuidSchema,
});

export const TimelineAnalysisCancelResponseSchema = z.object({
  success: z.boolean(),
  status: z.enum(['pending', 'running', 'completed', 'failed', 'cancelled']),
});
